<template>
  <v-row justify="center">
    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card>
        <v-alert v-if="error" tile type="error">{{ error }}</v-alert>

        <v-card-title class="primary white--text">
          {{ formTitle }}
        </v-card-title>

        <v-card-text>
          <v-stepper v-model="step" flat>
            <v-stepper-header style="box-shadow: none">
              <v-stepper-step :complete="step > 1" step="1">
                {{
                  step > 1
                    ? $t(`label.${this.selectedAction}`)
                    : $t("label.action")
                }}
              </v-stepper-step>
              <v-divider />
              <v-stepper-step :complete="step > 2" step="2">
                {{
                  selectedAction === "delete"
                    ? $t("label.confirm")
                    : $tc("label.name", 1)
                }}
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <!-- Step 1: Action Selection -->
              <v-stepper-content step="1" class="mt-0 pt-0">
                <v-form ref="actionForm">
                  <v-row>
                    <v-col cols="12" class="my-0 py-0">
                      <v-radio-group
                        v-model="selectedAction"
                        :rules="[rules.required]"
                        :label="`Je hebt nu de bookmark '${object?.name}' geselecteerd. Wil je deze bewerken, verwijderen of een nieuwe toevoegen?`"
                      >
                        <v-radio :label="$t('label.add')" value="add"></v-radio>
                        <v-radio
                          :label="$t('label.edit')"
                          value="edit"
                        ></v-radio>
                        <v-radio
                          :label="$t('label.delete')"
                          value="delete"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-form>
              </v-stepper-content>

              <!-- Step 2: Name Input / Delete Confirmation -->
              <v-stepper-content step="2" class="mt-0 pt-0">
                <!-- If deleting, show a confirmation message instead of the form -->

                <v-card v-if="selectedAction === 'delete'">
                  <v-card-title class="title">
                    <v-icon color="error" class="mr-2">warning</v-icon>
                    <slot name="title">{{ $t("deleteDialog.title") }}</slot>
                  </v-card-title>
                  <v-card-text>
                    <slot name="content">{{ $t("deleteDialog.content") }}</slot>
                  </v-card-text>
                </v-card>

                <!-- For add/edit show the name input form -->
                <v-form v-else ref="form" v-model="isValid">
                  <v-row>
                    <v-col cols="12" class="pt-5">
                      <v-text-field
                        v-model="editedItem.name"
                        :counter="100"
                        :rules="[rules.required, rules.maxLength(100)]"
                        v-bind:label="$tc('label.name', 1)"
                        outlined
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-form>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>

        <v-card-actions class="pb-5 pr-5">
          <v-spacer></v-spacer>
          <v-btn :disabled="isSaving" text rounded @click="close">{{
            $t("button.cancelBtn")
          }}</v-btn>
          <v-btn
            :disabled="isSaving"
            text
            rounded
            @click="onBack"
            v-if="step > 1 && canEdit"
          >
            {{ $t("button.backBtn") }}
          </v-btn>
          <v-btn
            outlined
            rounded
            color="primary"
            @click="onNext"
            v-if="step < 2"
          >
            {{ $t("button.nextBtn") }}
          </v-btn>
          <v-btn
            outlined
            rounded
            :loading="isSaving"
            :disabled="selectedAction !== 'delete' && !isValid"
            color="primary"
            @click="save"
            v-else
          >
            {{ $t("button.saveBtn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import gql from "graphql-tag";
import rules from "@/utils/rules.js";

export default {
  name: "report-filter-add-dialog",
  props: ["dialog", "state", "reportId", "object"],
  components: {},

  apollo: {},

  data() {
    return {
      error: null,
      isSaving: false,
      step: 1,
      selectedAction: "add",
      isValid: false,

      editedItem: {
        id: null,
        name: null,
      },
      defaultItem: {
        id: null,
        name: null,
      },
      rules: rules,
    };
  },
  computed: {
    me() {
      return this.$store.state.user.me;
    },

    canEdit() {
      return this.me && this.object && this.me?.id === this.object?.user?.id;
    },

    formTitle() {
      return (
        this.$t(`label.${this.selectedAction}`) +
        " " +
        this.$tc("label.bookmark", 1)
      );
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.step = 1;
      }

      this.resetForm();
    },

    selectedAction(val) {
      if (val === "edit") {
        this.editedItem.name = this.object?.name;
      } else {
        this.editedItem.name = null;
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      }
    },
  },
  created() {},
  methods: {
    resetForm() {
      this.error = null;
      this.isSaving = false;
      this.step = this.canEdit ? 1 : 2;
      this.selectedAction = "add";
      this.isValid = false;

      if (this.$refs.actionForm) {
        this.$refs.actionForm.resetValidation();
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));

      // when editing, set form with object
      if (this.dialog && this.canEdit) {
        this.editedItem.id = this.object.id;
      }
      // when new or closing, set form with default
      else {
        this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
      }
    },

    close() {
      this.$emit("update:dialog", false);
      this.resetForm();
    },

    onNext() {
      this.step++;
    },

    onBack() {
      this.step--;
    },

    delete() {
      this.error = null;
      this.isSaving = true;

      const payload = {
        ids: [this.object.id],
      };

      this.$apollo
        .mutate({
          mutation: gql`
            mutation deleteUserReportFilters(
              $input: DeleteUserReportFiltersInput!
            ) {
              deleteUserReportFilters(input: $input) {
                userReportFilters {
                  id
                }
              }
            }
          `,
          variables: {
            input: payload,
          },
        })
        .then((response) => {
          this.$emit("changed", response);
          this.close();
          this.$emit("deleted", response);
          
          const payload = {
            color: "success",
            message: `Bookmark successfully deleted`,
          };
          this.$store.dispatch("snackbar/showMessage", payload);
        })
        .catch((error) => {
          console.log(error);

          this.error = error.graphQLErrors
            .map((error) => error.message)
            .join(", ");
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    save() {
      // If deleting, bypass form validation and call delete mutation (or emit event)
      if (this.selectedAction === "delete") {
        this.delete();

        return;
      }

      // For add/edit actions, validate the form
      if (!this.$refs.form.validate()) {
        return;
      }

      // set form state
      this.error = null;
      this.isSaving = true;

      // prepare api call payload
      var payload = {
        name: this.editedItem?.name,
        state: this.state,
      };

      if (this.object && this.selectedAction === "edit") {
        // prepare graphql mutation payload
        payload.id = this.editedItem.id;

        this.$apollo
          .mutate({
            mutation: gql`
              mutation updateUserReportFilter(
                $input: UpdateUserReportFilterInput!
              ) {
                updateUserReportFilter(input: $input) {
                  userReportFilter {
                    id
                    name
                    state
                    type
                  }
                }
              }
            `,
            variables: {
              input: payload,
            },
          })
          .then((response) => {
            this.$emit("changed", response);
            this.close();

            const payload = {
              color: "success",
              message: `Bookmark successfully updated`,
            };
            this.$store.dispatch("snackbar/showMessage", payload);
          })
          .catch((error) => {
            console.log(error);

            this.error = error.graphQLErrors
              .map((error) => error.message)
              .join(", ");
          })
          .finally(() => {
            this.isSaving = false;
          });
      } else {
        payload.report = this.reportId;

        this.$apollo
          .mutate({
            mutation: gql`
              mutation createUserReportFilter(
                $input: CreateUserReportFilterInput!
              ) {
                createUserReportFilter(input: $input) {
                  userReportFilter {
                    id
                    name
                    state
                    type
                  }
                }
              }
            `,
            variables: {
              input: payload,
            },
          })
          .then((response) => {
            this.$emit("changed", response);
            this.close();

            const payload = {
              color: "success",
              message: `Bookmark successfully added`,
            };
            this.$store.dispatch("snackbar/showMessage", payload);
          })
          .catch((error) => {
            console.log(error);

            this.error = error.graphQLErrors
              .map((error) => error.message)
              .join(", ");
          })
          .finally(() => {
            this.isSaving = false;
          });
      }
    },
  },
};
</script>
